/**
 * Active Filters Component
 * Manages display and removal of active search filters
 */

const activeFilters = (settings = {}) => {
  return {
    settings,
    
    /**
     * Initialize the component
     */
    init() {
      // Watch for changes in applied filters
      this.$watch('$store.search.appliedFilters', (newFilters) => {
        this.updateActiveFilters(newFilters);
      });
    },

    /**
     * Check if there are any active filters
     */
    get hasActiveFilters() {
      const appliedFilters = this.$store.search.appliedFilters || [];
      return appliedFilters.length > 0;
    },

    /**
     * Get list of active filters with their values
     */
    get activeFiltersList() {
      const appliedFilters = this.$store.search.appliedFilters || [];
      const filterCatalogue = this.$store.search.filters || [];

      return appliedFilters.map(filter => {
        const catalogueFilter = filterCatalogue.find(f => f.id === filter.id);
        return {
          id: filter.id,
          label: filter.label || catalogueFilter?.label || filter.id,
          type: filter.type,
          activeValues: filter.values || []
        };
      }).filter(filter => filter.activeValues.length > 0);
    },

    /**
     * Update active filters (called when store changes)
     */
    updateActiveFilters(newFilters) {
      // This method can be used for any side effects when filters change
      // For now, the reactive getters handle the display updates
    },

    /**
     * Remove a specific filter value
     */
    async removeFilterValue(filterId, valueId) {
      try {
        const currentFilters = [...(this.$store.search.appliedFilters || [])];
        
        // Find the filter
        const filterIndex = currentFilters.findIndex(f => f.id === filterId);
        
        if (filterIndex === -1) return;
        
        const filter = currentFilters[filterIndex];
        
        // Remove the specific value
        filter.values = filter.values.filter(value => value.id !== valueId);
        
        // Remove the entire filter if no values left
        if (filter.values.length === 0) {
          currentFilters.splice(filterIndex, 1);
        }
        
        // Apply the updated filters
        await this.$store.search.applyFilters(currentFilters);
        
        // Dispatch event
        window.dispatchEvent(new CustomEvent('activeFilters:valueRemoved', {
          detail: {
            filterId,
            valueId,
            remainingFilters: currentFilters.length
          }
        }));
        
      } catch (error) {
        console.error('[ActiveFilters] Failed to remove filter value:', error);
      }
    },

    /**
     * Clear all active filters
     */
    async clearAllFilters() {
      try {
        // Clear all applied filters
        await this.$store.search.applyFilters([]);
        
        // Dispatch event
        window.dispatchEvent(new CustomEvent('activeFilters:allCleared', {
          detail: {
            previousFilterCount: this.activeFiltersList.length
          }
        }));
        
      } catch (error) {
        console.error('[ActiveFilters] Failed to clear all filters:', error);
      }
    },

    /**
     * Get formatted display text for a filter value
     */
    getValueDisplayText(filter, value) {
      // For price ranges, format the display text
      if (filter.type === 'PRICE_RANGE') {
        try {
          const input = JSON.parse(value.input);
          if (input.price) {
            const currency = Shopify?.currency?.active || '$';
            return `${currency}${input.price.min} - ${currency}${input.price.max}`;
          }
        } catch (e) {
          // Fallback to label
          return value.label;
        }
      }
      
      return value.label;
    },

    /**
     * Get count of total active filter values
     */
    get totalActiveFiltersCount() {
      return this.activeFiltersList.reduce((total, filter) => {
        return total + filter.activeValues.length;
      }, 0);
    },

    /**
     * Check if a specific filter has active values
     */
    hasActiveValues(filterId) {
      return this.activeFiltersList.some(filter => 
        filter.id === filterId && filter.activeValues.length > 0
      );
    }
  };
};

// Export for Alpine.js auto-loading
export default {
  name: 'activeFilters',
  component: activeFilters
};